import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useEffect } from "react";
import { Logo } from "@/components/Logo";
import { useAuth } from "@/contexts/AuthContext";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Sun, BarChart3, BookOpen, User, LogOut } from "lucide-react";

const NAV = [
  { to: "/me/today", label: "Today", icon: Sun, testid: "nav-today" },
  { to: "/me/progress", label: "Progress", icon: BarChart3, testid: "nav-progress" },
  { to: "/me/recipes", label: "Recipes", icon: BookOpen, testid: "nav-patient-recipes" },
  { to: "/me/profile", label: "Profile", icon: User, testid: "nav-profile" },
];

export default function PatientLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    // Session heartbeat — ping every 60s while the app is open so the dietitian
    // sees accurate "time spent on the app" per login session.
    let cancelled = false;
    const ping = () => {
      if (cancelled) return;
      if (document.visibilityState !== "visible") return;
      api.post("/patient/heartbeat").catch(() => {});
    };
    ping();
    const interval = setInterval(ping, 60000);
    document.addEventListener("visibilitychange", ping);
    return () => {
      cancelled = true;
      clearInterval(interval);
      document.removeEventListener("visibilitychange", ping);
    };
  }, []);

  const handleLogout = async () => {
    await logout();
    navigate("/login", { replace: true });
  };

  return (
    <div className="min-h-screen bg-background flex flex-col" data-testid="patient-layout">
      <header className="sticky top-0 z-30 bg-card border-b border-border px-4 sm:px-6 py-3 flex items-center justify-between">
        <Logo size="sm" />
        <div className="flex items-center gap-3">
          <div className="hidden sm:block text-right">
            <div className="text-sm font-semibold" data-testid="patient-name">{user?.name}</div>
            <div className="text-xs text-muted-foreground">{user?.email}</div>
          </div>
          <Button variant="ghost" size="sm" onClick={handleLogout} data-testid="patient-logout">
            <LogOut size={16} />
          </Button>
        </div>
      </header>

      <main className="flex-1 pb-24">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 py-6">
          <Outlet />
        </div>
      </main>

      <nav className="fixed bottom-0 left-0 right-0 z-30 bg-card border-t border-border flex items-center justify-around py-2 px-4 sm:px-6">
        {NAV.map((item) => {
          const Icon = item.icon;
          return (
            <NavLink
              key={item.to}
              to={item.to}
              data-testid={item.testid}
              className={({ isActive }) =>
                `flex flex-col items-center gap-1 px-4 py-2 text-xs font-medium ${
                  isActive ? "text-primary" : "text-muted-foreground"
                }`
              }
            >
              <Icon size={20} />
              <span>{item.label}</span>
            </NavLink>
          );
        })}
      </nav>
    </div>
  );
}
